import { InputAdornment } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useLayoutEffect, useState } from 'react';
import { useMutation } from '@apollo/client';

import { DELETE_MEDIA_LOGO, UPDATE_WORKSPACE } from '../../../apis/workspace';
import { DOMAIN_NAME } from '../../../constants/config';
import { uploadWorkspaceAvatar } from '../../../utils/upload-avatar';
import { WORKSPACE_NAME_REGEX } from '../../../constants/workspaces';
import Button from '../../../components/atoms/button';
import Input from '../../../components/atoms/input';
import Modal from '../../../components/organisms/modal';
import PageName from '../../../components/Heading/ProjectTitle';
import Toaster from '../../../components/Snackbar/Toaster';
import UploadAvatarCard from '../../../components/organisms/upload-avatar-card';
import useAuth from '../../../context/AuthContext';
import useLoader from '../../../context/LoaderContext';
import useWorkspace from '../../../context/WorkspaceContext';
import WorkspaceOptions from '../../../components/molecules/workspace-options';

const regex = new RegExp(WORKSPACE_NAME_REGEX);

const WorkspaceSettings = () => {
  // init
  const { setIsLoading } = useLoader();
  const { authUser } = useAuth();
  const { workspace, setWorkspace } = useWorkspace();

  // states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', autoHideDuration: 3000 });
  const [isUploading, setIsUploading] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceNameError, setWorkspaceNameError] = useState('');

  // mutations/queries
  const [updateWorkspace, { loading: isWorkspaceUpdating }] = useMutation(UPDATE_WORKSPACE);
  const [deleteMediaLogo, { loading: isAvatarDeleting }] = useMutation(DELETE_MEDIA_LOGO);

  const handleModalStatus = () => {
    setIsModalOpen((prevstate) => !prevstate);
  };

  const handleRemoveAvatar = async () => {
    if (workspace.logo) {
      try {
        const urlSplit = workspace.logo.split('/');
        const fileType = urlSplit[urlSplit.length - 1].split('.')[1];
        const variables = { type: 'workspace', fileType, userId: authUser?.id, workspaceId: workspace.id };
        await deleteMediaLogo({ variables });
        setIsModalOpen(false);
        setToast((prevState) => ({ ...prevState, open: true, type: 'success', message: 'Avatar removed successfully' }));
        setWorkspace({ ...workspace, logo: null });
        document.getElementById('favicon')?.setAttribute('href', '/favicon.ico');
      } catch (error) {
        setToast((prevState) => ({ ...prevState, open: true, type: 'error', message: 'Something went wrong while deleting Avatar' }));
      }
    }
  };

  const handleUploadAvatar = async (avatar) => {
    try {
      await uploadWorkspaceAvatar({ workspaceId: workspace.id, workspaceLogo: avatar });
      setWorkspace({ ...workspace, logo: avatar.dataURI });
      document.getElementById('favicon')?.setAttribute('href', avatar.dataURI);
      setToast((prevState) => ({ ...prevState, open: true, type: 'success', message: 'Avatar updated successfully' }));
    } catch (error) {
      setToast((prevState) => ({ ...prevState, open: true, type: 'error', message: 'Something went wrong while updating Avatar' }));
    }
  };

  // close the toast
  const handleLogoutSuccessClose = () => {
    setToast((prevstate) => ({ ...prevstate, open: false }));
  };

  const validateWorkspaceName = () => {
    if (workspaceName) {
      if (regex.test(workspaceName)) {
        setWorkspaceNameError('');
        return true;
      }
      setWorkspaceNameError('Workspace name must be between 8 and 36 characters and must not include any special characters.');
      return false;
    }
    setWorkspaceNameError('Workspace name is required');
    return false;
  };

  const handleWorkspaceNameChange = (event) => {
    if (regex.test(event.target.value)) setWorkspaceNameError('');
    setWorkspaceName(event.target.value);
  };

  const handleUpdateWorkspace = async () => {
    if (validateWorkspaceName()) {
      try {
        const variables = {
          input: {
            id: workspace.id,
            name: workspaceName,
            createdAt: workspace.createdAt,
            updatedAt: new Date().toISOString(),
          },
        };
        const { data: { updateWorkspace: newWorkspace } } = await updateWorkspace({ variables });
        setWorkspace(newWorkspace);
        setToast((prevState) => ({ ...prevState, open: true, type: 'success', message: 'Workspace updated successfully' }));
      } catch (error) {
        setToast((prevState) => ({ ...prevState, open: true, type: 'error', message: 'Something went wrong while updating Workspace name' }));
      }
    }
  };

  useLayoutEffect(() => {
    if (workspace) {
      document.title = `${workspace.name} | Settings`;
      setWorkspaceName(workspace.name);
      setIsLoading(false);
    }
  }, [workspace]);

  return (
    <>
      <div className="page-header">
        <WorkspaceOptions />
        <PageName name="Edit Workspace" />
      </div>
      <div className="settings-grid">
        <div className="grid-item">
          <div className="border-cards">
            <div className="border-card">
              <span className="border-card-title">What is the name of your workspace or company?</span>
              {/* Workspace name */}
              <div className="border-card-input-container">
                <span className="border-card-label">Workspace name</span>
                <Input error={!!workspaceNameError} helperText={workspaceNameError} value={workspaceName} onChange={handleWorkspaceNameChange} autoFocus placeHolder="Enter Workspace name" />
              </div>
              <Button isLoading={isWorkspaceUpdating} disabled={workspace?.name === workspaceName || isWorkspaceUpdating} onClick={handleUpdateWorkspace} fullWidth={false} primary outlined small>Edit</Button>
            </div>
          </div>
        </div>
        <div className="grid-item">
          <UploadAvatarCard
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            onRemove={handleModalStatus}
            onUpload={handleUploadAvatar}
            avatar={workspace?.logo}
            name={workspace?.name}
            title="Upload your workspace logo."
            uploadButtonText="Upload Logo"
            cancelButtonText="Remove"
          />
          <Modal
            title="Remove Avatar"
            body="Are you sure you want to remove workspace avatar?"
            secondaryButton={(<Button isLoading={isAvatarDeleting} disabled={isAvatarDeleting} onClick={handleRemoveAvatar} danger>Remove Avatar</Button>)}
            primaryButton={(<Button disabled={isAvatarDeleting} primary outlined onClick={handleModalStatus}>Cancel</Button>)}
            isOpen={isModalOpen}
            actionAlignment="right"
            setIsOpen={setIsModalOpen}
          />
        </div>
        <div className="grid-item">
          <div className="border-card">
            <div className="border-card-input-container">
              <span className="border-card-label">Add URL Slug</span>
              <Input
                readOnly
                value={workspace?.handle ? `${workspace?.handle}.${DOMAIN_NAME}` : ''}
                type="text"
                className="input"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <LockOutlined fontSize="small" style={{ color: 'black' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Toaster anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} {...toast} onClose={handleLogoutSuccessClose} />
    </>
  );
};

export default WorkspaceSettings;
